import { useCallback, cloneElement, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import useAuth from "../../context/useAuth.js";
import useSaveLoginInfo from "../../hooks/useSaveLoginInfo.js";
import { normalizeRole } from "../../utils/permissions.js";
import Avatar from "../ui/Avatar.jsx";
import RoleBadge from "../ui/RoleBadge.jsx";
import Switch from "../ui/Switch.jsx";

/**
 * Account popover for the top-right header user pill.
 *
 * Deliberately minimal: the summary shows only the avatar, name and role badge
 * (no email/phone), and the actions are the three the header genuinely needs —
 * open the full profile, toggle "Save Login Info", and sign out. Everything
 * else (security, staff passwords, salon business) lives inside `/profile`.
 *
 * The panel is rendered through a portal with fixed positioning measured from
 * the trigger, so it is never clipped by a parent `overflow-hidden`. Closes on
 * outside click, Escape, resize and scroll.
 */
export default function UserMenu({ children }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { enabled: saveLogin, pending: savingPref, setEnabled: setSaveLogin } =
    useSaveLoginInfo();

  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);

  const role = normalizeRole(user);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const panel =
    open && coords
      ? createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label="Account menu"
            style={{
              position: "fixed",
              width: 320,
              maxWidth: "calc(100vw - 1.5rem)",
              ...coords,
            }}
            className="z-[60] origin-top-right animate-scale-in overflow-hidden rounded-2xl border border-outline-variant/15 bg-surface-container-lowest shadow-ambient-lg motion-reduce:animate-none"
          >
            <div className="flex items-center gap-3 bg-surface-container-low px-5 py-4">
              <Avatar src={user?.avatar} name={user?.name} size="lg" ring />
              <div className="min-w-0">
                <p className="truncate font-headline text-base font-bold text-on-surface">
                  {user?.name || "Guest"}
                </p>
                <div className="mt-1">
                  <RoleBadge role={role} />
                </div>
              </div>
            </div>

            <div className="p-2">
              <MenuItem
                icon="account_circle"
                label="My Profile & Account"
                onClick={() => go("/profile")}
              />
              <div className="flex min-h-[48px] items-center gap-3 rounded-xl px-3 py-2.5">
                <span
                  className="material-symbols-outlined text-lg text-on-surface-variant"
                  aria-hidden="true"
                >
                  bookmark
                </span>
                <span className="flex-1 text-sm font-medium text-on-surface">
                  Save Login Info
                </span>
                <Switch
                  checked={saveLogin}
                  disabled={savingPref}
                  onChange={setSaveLogin}
                  label="Save login info on this device"
                />
              </div>
            </div>

            <div className="border-t border-outline-variant/10 p-2">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  signOut();
                }}
                className="flex min-h-[48px] w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-error transition-colors hover:bg-error-container/60 active:scale-[0.99]"
              >
                <span className="material-symbols-outlined text-lg" aria-hidden="true">
                  logout
                </span>
                Sign Out
              </button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {cloneElement(children, {
        ref: triggerRef,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        onClick: (event) => {
          children.props.onClick?.(event);
          setOpen((prev) => !prev);
        },
      })}
      {panel}
    </>
  );
}

function MenuItem({ icon, label, onClick }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex min-h-[48px] w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-on-surface transition-colors hover:bg-surface-container active:scale-[0.99]"
    >
      <span className="material-symbols-outlined text-lg text-on-surface-variant" aria-hidden="true">
        {icon}
      </span>
      <span className="flex-1">{label}</span>
      <span className="material-symbols-outlined text-base text-on-surface-variant/60" aria-hidden="true">
        chevron_right
      </span>
    </button>
  );
}
